// Spec markdown → safe HTML: the same in the editor preview, on vendor documents pages and anywhere else a
// spec is shown. Raw HTML in the text is shown as text (never rendered); links must be http(s), relative,
// mailto or #anchors; images must be our own /docs/img/<sha256> addresses (see server/src/docs-images.ts) or https.
import { Marked, type Token, type Tokens } from "marked";

const ESC: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ESC[c]);
export const safeHref = (href: string) => (/^(https?:|mailto:|#|\/(?!\/))/i.test(href.trim()) ? href.trim() : null);
export const safeSrc = (src: string) => (/^(\/docs\/img\/[0-9a-f]{64}(\?[\w=&.-]*)?|https:\/\/)/i.test(src.trim()) ? src.trim() : null);

const md = new Marked({ gfm: true, async: false });
md.use({
  renderer: {
    html(token: Tokens.HTML | Tokens.Tag) {
      return escapeHtml(token.text);
    },
    link(token: Tokens.Link) {
      const inner = this.parser.parseInline(token.tokens);
      const href = safeHref(token.href);
      if (!href) return inner;
      const title = token.title ? ` title="${escapeHtml(token.title)}"` : "";
      return `<a href="${escapeHtml(href)}"${title} rel="noopener noreferrer" target="_blank">${inner}</a>`;
    },
    image(token: Tokens.Image) {
      const src = safeSrc(token.href);
      if (!src) return escapeHtml(token.text);
      const title = token.title ? ` title="${escapeHtml(token.title)}"` : "";
      return `<img src="${escapeHtml(src)}" alt="${escapeHtml(token.text)}"${title} loading="lazy">`;
    },
  },
});

// Callouts a vendor can't miss ("the insert MUST be ferromagnetic"), styled by .callout-warning / .callout-info:
//   :::warning Optional title
//   Any markdown (but no line starting with :::)
//   :::
// Rendering stays linear in the text however it's written (it runs on every keystroke and on vendor pages): a
// callout ends at the next line starting with ":::", which must be the closing ":::" or the opener is just text,
// and paragraphs are kept off opener lines by a blank line put before them (separateCallouts) rather than by a
// `start` hook, which marked would call for every paragraph to scan the rest of the document.
export type Callout = { type: "callout"; raw: string; kind: string; title: Tokens.Generic["tokens"]; tokens: Tokens.Generic["tokens"] };
const OPENER = /^:::(warning|info)\b[ \t]*([^\n]*)(?:\n|$)/i;
const OPENER_LINE = /^:::(warning|info)\b/i;
// A fence opens as marked opens one (a backtick fence's info string has no backtick) and closes only on a line of the
// same run or longer and nothing else, so a "```" line inside "````" code doesn't end it. (Lines may still end in "\r":
// marked takes those off itself, after this.)
const FENCE = /^ {0,3}(`{3,}(?=[^`]*$)|~{3,})/;
const closesFence = (line: string, fence: string) => {
  const at = line.indexOf(fence);
  return at >= 0 && at <= 3 && /^ *$/.test(line.slice(0, at)) && /^[~`]* *\r?$/.test(line.slice(at + fence.length));
};

/** Where the callout whose body starts at `from` ends: the body's end and the end of its closing line (and blank lines after). */
function calloutEnd(src: string, from: number): { body: number; end: number } | null {
  if (src[from - 1] !== "\n") return null;
  const nl = src.indexOf("\n:::", from - 1);
  if (nl < 0) return null;
  let eol = src.indexOf("\n", nl + 1);
  if (eol < 0) eol = src.length;
  // Anything but a bare ":::" (another opener, ::::…) leaves this callout unclosed.
  if (!/^:::[ \t]*$/.test(src.slice(nl + 1, eol))) return null;
  let end = eol;
  while (src[end] === "\n") end++;
  return { body: nl, end };
}

/**
 * A blank line before each callout opener outside fenced code, so the paragraph above never takes it in. `inserted`
 * collects where each added newline is in the result.
 */
function separateCallouts(source: string, inserted?: number[]): string {
  if (!source.includes(":::")) return source;
  const lines = source.split("\n");
  let fence = "";
  let at = 0;
  for (let i = 0; i < lines.length; i++) {
    const f = fence ? null : FENCE.exec(lines[i]);
    if (fence) {
      if (closesFence(lines[i], fence)) fence = "";
    } else if (f) fence = f[1];
    else if (i > 0 && lines[i - 1].trim() && OPENER_LINE.test(lines[i])) {
      inserted?.push(at);
      lines[i] = `\n${lines[i]}`;
    }
    at += lines[i].length + 1;
  }
  return lines.join("\n");
}

md.use({
  extensions: [
    {
      name: "callout",
      level: "block",
      tokenizer(src: string) {
        const m = OPENER.exec(src);
        if (!m) return undefined;
        const close = calloutEnd(src, m[0].length);
        if (!close) return undefined;
        return {
          type: "callout",
          raw: src.slice(0, close.end),
          kind: m[1].toLowerCase(),
          title: this.lexer.inlineTokens(m[2].trim()),
          tokens: this.lexer.blockTokens(src.slice(m[0].length, close.body), []),
        };
      },
      renderer(token) {
        const t = token as unknown as Callout;
        const title = t.title?.length ? `<p class="callout-title">${this.parser.parseInline(t.title)}</p>` : "";
        return `<div class="callout callout-${t.kind}" role="note">${title}${this.parser.parse(t.tokens ?? [])}</div>\n`;
      },
    },
  ],
});

/**
 * The text without its empty sections (Mitch, 2026-11-04: the starting template's headings, "most of the time that
 * information will be ignored, so it creates clutter"): a ## (or lower) heading with nothing under it before the next heading of its
 * level or above goes, and so does one whose only content is a table with no values in it (the template's "| Specification |
 * Value |" with one blank row). Code blocks are left as they are. What's written stays exactly as written.
 */
export function withoutEmptySections(source: string): string {
  let lines = source.split("\n");
  const fence = /^\s*(```|~~~)/;
  const levelAt = (ls: string[]) => {
    let inCode = false;
    return ls.map((l) => {
      if (fence.test(l)) inCode = !inCode;
      return inCode ? 0 : (/^(#{1,6})\s/.exec(l)?.[1].length ?? 0);
    });
  };
  const blankTable = (rows: string[]) =>
    rows.length >= 2 && rows.every((r) => r.trim().startsWith("|")) && /^[\s|:-]+$/.test(rows[1]) && rows.slice(2).every((r) => /^[\s|]*$/.test(r));
  for (let changed = true; changed; ) {
    changed = false;
    const level = levelAt(lines);
    for (let i = 0; i < lines.length; i++) {
      // A section (## and below): a document's own title (#) stays even with nothing under it
      if (level[i] < 2) continue;
      let end = lines.length;
      for (let j = i + 1; j < lines.length; j++)
        if (level[j]) {
          end = j;
          break;
        }
      // Only a section with nothing under it at all: a heading followed by its own subheadings isn't empty
      if (end < lines.length && level[end] > level[i]) continue;
      const body = lines.slice(i + 1, end).filter((l) => l.trim() !== "");
      if (body.length === 0 || blankTable(body)) {
        lines = [...lines.slice(0, i), ...lines.slice(end)];
        changed = true;
        break;
      }
    }
  }
  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+/, "");
}

export function renderMarkdown(source: string): string {
  return md.parse(separateCallouts(source)) as string;
}

/**
 * The top-level tokens renderMarkdown renders for `source` (line endings already "\n"), and where in the lexed text
 * the newlines separateCallouts added are, so shared/markdown-doc.ts can map each token back to the source.
 */
export function lexMarkdown(source: string): { tokens: Token[]; inserted: number[] } {
  const inserted: number[] = [];
  return { tokens: md.lexer(separateCallouts(source, inserted)), inserted };
}
